import { usePostHog } from '@posthog/react';
import React, { useId } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { AiHero, type AiHeroSize } from 'components/ai/AiHero';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import useAuth from 'hooks/useAuth';
import { FOCUS_RING, FOCUS_RING_INSET } from 'layout/NewDesignLayout/focusRing';
import { FeatureFlag } from 'types/features';
import { cn } from 'utils/cn';
import { familyTileClass } from 'utils/families';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { PlaceholderFillConfig } from '../hooks/usePlaceholderFill';
import {
    getRunnablePrompts,
    getSamplePrompts,
    getSuggestedPrompts,
    PHONE_PROMPT_COUNT,
    PromptOption,
    SAMPLE_PROMPT_GROUPS,
    SamplePrompt,
} from '../samplePrompts';
import AiChatCapabilities, { AiChatCapabilitiesLink, TEXTS_REVEAL, useCapabilitiesView } from './AiChatCapabilities';
import AiChatResumeCard from './AiChatResumeCard';
import PromptBadges from './PromptBadges';

/** What a picked prompt hands the surface: its text, its picker set-up, and its id for the chat it starts. */
export type PromptSelectHandler = (
    text: string,
    optionsMap?: Record<string, PromptOption[]>,
    config?: PlaceholderFillConfig,
    promptId?: string
) => void;

// The day's parts as the dashboard greets: morning until noon, afternoon until 18:00.
const greetingIdForNow = (hour = new Date().getHours()): string =>
    hour < 12
        ? 'ai-chat.hero.greeting.morning'
        : hour < 18
          ? 'ai-chat.hero.greeting.afternoon'
          : 'ai-chat.hero.greeting.evening';

/**
 * The greeting hero of an AI empty state: `greetingId` with the user's first name, and the question under it. A
 * profile with no first name gets the question as its greeting.
 */
export const AiChatGreeting: React.FC<{ size: AiHeroSize; greetingId: string; className?: string }> = ({
    size,
    greetingId,
    className,
}) => {
    const intl = useIntl();
    const { user } = useAuth();
    const line = intl.formatMessage({ id: 'ai-chat.hero.line' });

    return (
        <AiHero
            size={size}
            eyebrow={intl.formatMessage({ id: 'ai-chat.title' })}
            greeting={user?.firstName ? intl.formatMessage({ id: greetingId }, { name: user.firstName }) : line}
            line={user?.firstName ? line : undefined}
            className={className}
        />
    );
};

const PromptTile: React.FC<{ prompt: SamplePrompt }> = ({ prompt: { icon: Icon, family } }) => (
    <span className={cn('grid size-7 shrink-0 place-items-center rounded-[8px]', familyTileClass(family))}>
        <Icon className="size-4" />
    </span>
);

interface AiChatSamplePromptsProps {
    onPromptSelect: PromptSelectHandler;
    /** The AI page's home (hero and gallery), or the phone's (hero and the first Suggested prompts). */
    variant?: 'page' | 'phone';
    /** The page's column with the history hidden: wider, and each card shows its preview. */
    wide?: boolean;
    /** The phone's "Continue previous chat" card under the hero, opening the chat through this; the page shows none. */
    onSelectThread?: (threadId: number) => void;
}

/** The AI page's and the phone's empty state: the greeting and the ready-made prompts, or What it can do. */
const AiChatSamplePrompts: React.FC<AiChatSamplePromptsProps> = ({
    onPromptSelect,
    variant = 'page',
    wide = false,
    onSelectThread,
}) => {
    const intl = useIntl();
    const posthog = usePostHog();
    const { user } = useAuth();
    const demoOverridesEnabled = useIsFeatureEnabled(FeatureFlag.AI_CHAT_DEMO_PLACEHOLDER_DEFAULTS);
    const { view, show, leaveClassName } = useCapabilitiesView();
    const listId = useId();

    const permissionCodes = user?.permissions?.map((permission) => permission.code) ?? [];
    const prompts = getRunnablePrompts(getSamplePrompts(demoOverridesEnabled), permissionCodes);
    const suggested = getSuggestedPrompts(prompts);

    const buildPromptText = (prompt: SamplePrompt): string => {
        return prompt.instructions ? `${prompt.prompt}\n\n${prompt.instructions}` : prompt.prompt;
    };

    const buildPlaceholderConfig = (prompt: SamplePrompt): PlaceholderFillConfig | undefined => {
        if (!prompt.placeholderDefaults && !prompt.placeholderInputTypes && !prompt.placeholderNoteId) {
            return undefined;
        }

        return {
            defaults: prompt.placeholderDefaults,
            placeholderInputTypes: prompt.placeholderInputTypes,
            noteId: prompt.placeholderNoteId,
        };
    };

    // Demo overrides widen a period to a phrase the standard picker does not carry ("this and last
    // year"). Offering it alongside the standard values keeps the starting value highlighted, and
    // reselectable after trying another one. Mirrors getPromptFill in floating/pagePrompts.ts.
    const buildOptions = (prompt: SamplePrompt, placeholder: string): PromptOption[] => {
        const options = prompt.options ?? [];
        const defaultValue = prompt.placeholderDefaults?.[placeholder];
        if (!defaultValue || options.some((option) => option.value === defaultValue)) {
            return options;
        }
        return [...options, { label: defaultValue[0].toUpperCase() + defaultValue.slice(1), value: defaultValue }];
    };

    const handlePromptClick = (prompt: SamplePrompt) => {
        posthog.capture('ai_sample_prompt_selected', { prompt_id: prompt.id, surface: 'page' });

        const text = buildPromptText(prompt);
        const config = buildPlaceholderConfig(prompt);

        if (prompt.options?.length) {
            // Bind the options to their placeholder. Most prompts have exactly one, so the first
            // match is right; a prompt whose earlier placeholder has its own picker names the
            // target explicitly, otherwise the chips would replace that picker.
            const placeholders = [...text.matchAll(/\[([^\]]+)\]/g)].map((match) => match[1]);
            const target = prompt.optionsPlaceholder ?? placeholders[0];
            if (target && placeholders.includes(target)) {
                const optionsMap: Record<string, PromptOption[]> = {
                    [target]: buildOptions(prompt, target),
                };
                onPromptSelect(text, optionsMap, config, prompt.id);
                return;
            }
        }

        onPromptSelect(text, undefined, config, prompt.id);
    };

    const previewOf = (prompt: SamplePrompt) =>
        intl.formatMessage({ id: prompt.previewTextId, defaultMessage: prompt.previewText });

    const capabilitiesLink = (
        <AiChatCapabilitiesLink onClick={() => show('capabilities')} autoFocus={view === 'returned'} />
    );

    const promptGrid = (gridPrompts: SamplePrompt[]) => (
        <div className="grid grid-cols-2 gap-2 @2xl:grid-cols-3">
            {gridPrompts.map((prompt) => (
                <button
                    key={prompt.id}
                    type="button"
                    onClick={() => handlePromptClick(prompt)}
                    className={cn(
                        'border-border bg-card hover:border-border-control new:border-0 new:shadow-card new:not-focus-visible:outline-hidden new:hover:shadow-peek new:transition-[box-shadow] flex min-h-12 cursor-pointer gap-2.5 rounded-[18px] border p-2.5 text-start transition-colors',
                        wide ? 'items-start' : 'items-center',
                        FOCUS_RING
                    )}
                >
                    <PromptTile prompt={prompt} />
                    <span className="flex min-w-0 flex-1 flex-col">
                        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                            {/* Wraps rather than truncates: a card's title is the prompt's name. */}
                            <span className="text-meta text-foreground font-semibold text-pretty">
                                <FormattedMessage id={prompt.titleId} defaultMessage={prompt.title} />
                            </span>
                            <PromptBadges badges={prompt.badges} />
                        </span>
                        {/* One line, so every card in a row keeps the same height; the whole line is its title. */}
                        {wide ? (
                            <span title={previewOf(prompt)} className="text-caption text-foreground-tertiary truncate">
                                {previewOf(prompt)}
                            </span>
                        ) : null}
                    </span>
                </button>
            ))}
        </div>
    );

    const body =
        view === 'capabilities' ? (
            <AiChatCapabilities onBack={() => show('returned')} className={leaveClassName} />
        ) : variant === 'phone' ? (
            <div className={cn('flex flex-col gap-6', leaveClassName)}>
                <AiChatGreeting size="compact" greetingId="ai-chat.hero.title" />
                {onSelectThread ? <AiChatResumeCard surface="page" onSelectThread={onSelectThread} /> : null}
                <section aria-labelledby={listId} className="flex flex-col gap-1">
                    <div className="flex items-baseline justify-between gap-2 pb-1">
                        <h3 id={listId} className="text-label text-muted-foreground uppercase">
                            <FormattedMessage id="ai-chat.mobile.try-asking" />
                        </h3>
                        {capabilitiesLink}
                    </div>
                    <ul className="-mx-2 flex flex-col">
                        {suggested.slice(0, PHONE_PROMPT_COUNT).map((prompt) => (
                            <li key={prompt.id}>
                                <button
                                    type="button"
                                    onClick={() => handlePromptClick(prompt)}
                                    className={cn(
                                        'hover:bg-row-stripe flex min-h-11 w-full cursor-pointer items-center gap-3 rounded-[12px] px-2 py-1.5 text-start transition-colors',
                                        FOCUS_RING_INSET
                                    )}
                                >
                                    <PromptTile prompt={prompt} />
                                    <span className="text-foreground min-w-0 flex-1 font-medium text-pretty">
                                        {previewOf(prompt)}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            </div>
        ) : (
            <div className={cn('flex flex-col gap-6', leaveClassName)}>
                <AiChatGreeting size="page" greetingId={greetingIdForNow()} className={TEXTS_REVEAL} />
                {/* Each tab panel mounts as its tab opens, so the reveal sits on what stays: the strip and the
                    panels' wrapper. */}
                <Tabs defaultValue="suggested" className="@container gap-3">
                    <div
                        className={cn(
                            'flex items-center justify-between gap-3',
                            TEXTS_REVEAL,
                            'delay-[calc(var(--duration-micro)*2)]'
                        )}
                    >
                        <TabsList>
                            <TabsTrigger value="suggested">
                                <FormattedMessage id="ai-chat.sample-prompts.groups.suggested" />
                            </TabsTrigger>
                            {SAMPLE_PROMPT_GROUPS.map((group) => (
                                <TabsTrigger key={group.id} value={group.id}>
                                    <FormattedMessage id={group.titleId} defaultMessage={group.title} />
                                </TabsTrigger>
                            ))}
                        </TabsList>
                        {capabilitiesLink}
                    </div>
                    <div className={cn(TEXTS_REVEAL, 'delay-[calc(var(--duration-micro)*3)]')}>
                        <TabsContent value="suggested">{promptGrid(suggested)}</TabsContent>
                        {SAMPLE_PROMPT_GROUPS.map((group) => (
                            <TabsContent key={group.id} value={group.id}>
                                {promptGrid(prompts.filter((prompt) => prompt.group === group.id))}
                            </TabsContent>
                        ))}
                    </div>
                </Tabs>
            </div>
        );

    if (variant === 'phone') {
        return body;
    }

    // The column widens with the history hidden, in one step with the history's column and the composer: once the
    // list has left, and at once as it comes back.
    return (
        <div
            className={cn(
                'mx-auto w-full transition-[max-width] duration-0',
                wide ? 'max-w-[1040px] delay-(--resize-dur) motion-reduce:delay-(--duration-quick)' : 'max-w-[720px]'
            )}
        >
            {body}
        </div>
    );
};

export default AiChatSamplePrompts;
